import React, { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Mail, MessageCircle, Phone, Siren, X } from "lucide-react";
import { BRAND } from "@/lib/site";

const EASE = [0.22, 1, 0.36, 1];
const RED = "#D81E2F";

const LINKS = [
  {
    id: "call",
    label: "Sofort anrufen",
    sub: BRAND.phone,
    href: BRAND.phoneHref,
    icon: Phone,
    tone: "red",
  },
  {
    id: "whatsapp",
    label: "WhatsApp senden",
    sub: "Standort & Fläche schicken",
    href: BRAND.whatsapp,
    icon: MessageCircle,
    tone: "wa",
    external: true,
  },
  {
    id: "email",
    label: "E-Mail schreiben",
    sub: BRAND.email,
    href: BRAND.emailHref,
    icon: Mail,
    tone: "plain",
  },
];

const toneCls = {
  red: "bg-[#D81E2F] text-white hover:bg-[#ec2a3c]",
  wa: "bg-[#25D366] text-white hover:bg-[#2ee272]",
  plain: "bg-white text-[#062B52] border border-[#DCE5EF] hover:border-[#0757B8]",
};

const EmergencyDock = () => {
  const [open, setOpen] = useState(false);
  const [hidden, setHidden] = useState(false);

  // Dock ausblenden, solange das Notruf-Band im Footer sichtbar ist
  useEffect(() => {
    const band = document.querySelector('[data-testid="footer-emergency-band"]');
    if (!band || typeof IntersectionObserver === "undefined") return;
    const io = new IntersectionObserver(
      ([entry]) => {
        setHidden(entry.isIntersecting);
        if (entry.isIntersecting) setOpen(false);
      },
      { threshold: 0.12 },
    );
    io.observe(band);
    return () => io.disconnect();
  }, []);

  return (
    <div
      data-testid="emergency-dock"
      className={`fixed bottom-7 right-6 z-[45] hidden transition-all duration-500 lg:block ${
        hidden ? "pointer-events-none translate-y-4 opacity-0" : "translate-y-0 opacity-100"
      }`}
      aria-hidden={hidden}
    >
      <div className="relative flex flex-col items-end">
        {/* ---------- Trigger ---------- */}
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          data-testid="emergency-dock-toggle"
          aria-expanded={open}
          aria-label="Notruf 24/7 im Einsatz – Kontaktmöglichkeiten öffnen"
          className="group relative flex items-center gap-3 overflow-hidden rounded-2xl p-[2px] shadow-[0_22px_50px_-16px_rgba(216,30,47,0.7)] transition-all duration-500 hover:-translate-y-0.5"
          style={{ background: RED }}
        >
          {/* shine sweep */}
          <span className="pointer-events-none absolute inset-0 bg-[linear-gradient(115deg,transparent_38%,rgba(255,255,255,0.26)_50%,transparent_62%)] bg-[length:230%_100%] bg-[position:125%_0] transition-[background-position] duration-[900ms] group-hover:bg-[position:-25%_0]" />
          <span className="relative flex items-center gap-3 rounded-[14px] bg-[#D81E2F] py-2 pl-2 pr-4 ring-1 ring-inset ring-white/15">
            <span className="relative flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-white/15">
              {open ? <X className="h-[18px] w-[18px]" /> : <Siren className="h-[18px] w-[18px]" />}
              {!open && (
                <span className="absolute inset-0 animate-ping rounded-xl bg-white/25" />
              )}
            </span>
            <span className="flex flex-col items-start leading-none">
              <span className="flex items-center gap-1.5">
                <span className="relative flex h-1.5 w-1.5">
                  <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-white/80" />
                  <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-white" />
                </span>
                <span className="font-mono-label text-[8.5px] text-white/80">Notruf 24/7</span>
              </span>
              <span
                className="mt-1 text-[13.5px] font-bold tracking-tight text-white"
                style={{ fontFamily: "Archivo, sans-serif" }}
              >
                Im Einsatz · melden
              </span>
            </span>
          </span>
        </button>

        {/* ---------- Panel ---------- */}
        <AnimatePresence>
          {open && (
            <motion.div
              data-testid="emergency-dock-panel"
              initial={{ opacity: 0, y: 12, x: 0, scale: 0.97 }}
              animate={{ opacity: 1, y: 0, x: 0, scale: 1 }}
              exit={{ opacity: 0, y: 12, scale: 0.97 }}
              transition={{ duration: 0.42, ease: EASE }}
              className="absolute bottom-full right-0 mb-3 w-[min(86vw,320px)] origin-bottom-right overflow-hidden rounded-2xl bg-white shadow-[0_44px_100px_-36px_rgba(6,43,82,0.7)] ring-1 ring-[#DCE5EF]"
            >
              <div className="relative overflow-hidden px-5 pb-4 pt-4 text-white" style={{ background: RED }}>
                <span className="pointer-events-none absolute -right-6 -top-8 h-24 w-24 rounded-full bg-white/10" />
                <div className="relative flex items-center gap-2">
                  <span className="relative flex h-2 w-2">
                    <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-white/70" />
                    <span className="relative inline-flex h-2 w-2 rounded-full bg-white" />
                  </span>
                  <p className="font-mono-label text-[9.5px] text-white">Notruf 24/7 im Einsatz</p>
                </div>
                <p className="relative mt-2 text-[15px] font-bold leading-snug" style={{ color: "#fff" }}>
                  Schnee oder Glätte akut? Direkt melden.
                </p>
              </div>

              <div className="p-4">
                <p className="text-[11.5px] leading-relaxed text-[#66788A]">
                  Bereitschaft für betreute Objekte in Bremen – Einsätze erfolgen nach
                  vereinbarten Prioritäten.
                </p>
                <div className="mt-3.5 grid gap-2">
                  {LINKS.map((l) => (
                    <a
                      key={l.id}
                      href={l.href}
                      data-testid={`emergency-${l.id}`}
                      {...(l.external ? { target: "_blank", rel: "noopener noreferrer" } : {})}
                      onClick={() => setOpen(false)}
                      className={`flex items-center gap-3 rounded-xl px-3.5 py-3 transition-all duration-300 hover:-translate-y-0.5 ${toneCls[l.tone]}`}
                    >
                      <l.icon className="h-4 w-4 shrink-0" />
                      <span className="min-w-0">
                        <span className="block text-[13px] font-semibold leading-tight">{l.label}</span>
                        <span
                          className={`mt-0.5 block truncate text-[11px] leading-tight ${
                            l.tone === "plain" ? "text-[#66788A]" : "text-white/85"
                          }`}
                        >
                          {l.sub}
                        </span>
                      </span>
                    </a>
                  ))}
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default EmergencyDock;
